import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { ShoppingCart, Trash2, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

export default function CartDrawer() {
  const { items, remove, clear, drawerOpen, setDrawerOpen } = useCart();
  const navigate = useNavigate();

  const gstRate = 0.18;
  const totalGross = items.reduce((sum, p) => {
    if (p.gst_included) return sum + p.base_amount;
    return sum + Math.round(p.base_amount * (1 + gstRate));
  }, 0);

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col bg-white">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-[color:var(--ink)]">
            <ShoppingCart size={20} className="text-[color:var(--brand-green)]" /> Your Cart
          </SheetTitle>
          <SheetDescription>
            {items.length === 0 ? 'Your cart is empty.' : `${items.length} plan(s) in cart`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4 space-y-3">
          {items.length === 0 && (
            <div className="py-14 text-center text-sm text-[color:var(--ink-soft)]">
              Add a plan from the Pricing page to start.
            </div>
          )}
          {items.map((p) => (
            <div key={p.id} className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
              <div className="flex justify-between gap-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-green-dark)]">
                    {p.duration}
                  </div>
                  <div className="font-bold text-sm text-[color:var(--ink)] mt-1">{p.name}</div>
                  <div className="text-xs text-[color:var(--ink-soft)] mt-1">
                    {p.gst_included ? '₹' : '₹'}
                    {p.base_amount.toLocaleString('en-IN')}
                    {p.gst_included ? ' (incl. GST)' : ' + GST'}
                  </div>
                </div>
                <button
                  onClick={() => remove(p.id)}
                  className="self-start w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center transition"
                  aria-label="Remove"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-neutral-100 pt-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[color:var(--ink-soft)]">Estimated total (incl. GST)</span>
              <span className="font-black text-lg text-[color:var(--brand-green)]">
                ₹{totalGross.toLocaleString('en-IN')}
              </span>
            </div>
            <p className="text-[11px] text-[color:var(--ink-soft)]">
              Note: For now, only one plan can be checked out at a time. Please buy plans individually.
              Apply coupons on the checkout screen.
            </p>
            <button
              onClick={() => {
                setDrawerOpen(false);
                navigate('/pricing');
              }}
              className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-green)] text-white font-semibold py-3 rounded-full text-sm hover:bg-[color:var(--brand-green-dark)] transition"
            >
              Go to Pricing to Checkout <ArrowRight size={16} />
            </button>
            <button
              onClick={clear}
              className="w-full text-xs text-[color:var(--ink-soft)] hover:text-red-500 transition"
            >
              Clear cart
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
